import { useState, useEffect, useCallback } from 'react'
import { api } from '../api'
import { useT } from '../i18n'
import { ErrorState, errorMessage } from './ErrorState'
import type { QueueStatsData } from '../types'

export function QueueStats() {
  const t = useT()
  const [data, setData] = useState<QueueStatsData | null>(null)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      setData(await api.getQueueStats())
      setError(null)
    } catch (err: unknown) {
      setError(errorMessage(err))
    }
  }, [])

  useEffect(() => {
    void refresh()
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') void refresh()
    }, 10_000)
    return () => clearInterval(interval)
  }, [refresh])

  if (error && !data) return <ErrorState message={error} onRetry={refresh} compact />
  if (!data) return null

  const items = [
    { key: 'waiting', label: t('queue.waiting'), value: data.waiting, color: 'var(--text-muted)' },
    { key: 'active', label: t('queue.active'), value: data.active, color: 'var(--info)' },
    { key: 'completed', label: t('queue.completed'), value: data.completed, color: 'var(--ok-fg)' },
    { key: 'failed', label: t('queue.failed'), value: data.failed, color: 'var(--crit)' },
    { key: 'delayed', label: t('queue.delayed'), value: data.delayed, color: 'var(--warn)' },
  ]

  return (
    <div className="queue-bar">
      <span className="queue-title">{t('queue.title')}</span>
      {items.map((item) => (
        <span key={item.key} className="queue-item">
          <span className="queue-dot" style={{ background: item.color }} />
          {item.label}: <strong>{item.value}</strong>
        </span>
      ))}
    </div>
  )
}
